import ChartHeader from '../ChartHeader';
import NoResponsesAvailableYet from '../NoResponsesAvailableYet';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { Box } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';

const SingleCompletionOverTimeChart = ({ data, onRefresh, isFirstLoad }) => {
  const chartRef = useRef(null);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  const handleManualRefresh = async () => {
    setIsManualRefreshing(true);
    if (onRefresh) {
      await onRefresh();
    }
    setTimeout(() => setIsManualRefreshing(false), 500);
  };

  useLayoutEffect(() => {
    if (!data || data.length === 0) return;

    // Create root
    const root = am5.Root.new(chartRef.current);

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Remove logo
    root._logo.dispose();

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        layout: root.verticalLayout
      })
    );

    // Add cursor
    const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
    cursor.lineY.set('visible', false);

    // Create axes
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxZoomCount: 1000,
        baseInterval: {
          timeUnit: 'day',
          count: 1
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minorGridEnabled: true
        }),
        tooltip: am5.Tooltip.new(root, {})
      })
    );

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        renderer: am5xy.AxisRendererY.new(root, {
          strokeDasharray: []
        })
      })
    );

    // Add X-axis title (Date) - positioned below the axis
    xAxis.children.push(
      am5.Label.new(root, {
        text: 'Date',
        x: am5.p50,
        centerX: am5.p50,
        fontSize: 14,
        fontWeight: 'bold',
        fill: am5.color('#666666'),
        paddingTop: 15
      })
    );

    // Add Y-axis title (Number of responses)
    yAxis.children.unshift(
      am5.Label.new(root, {
        text: 'Number of responses',
        rotation: -90,
        y: am5.p50,
        centerX: am5.p50,
        fontSize: 14,
        fontWeight: 'bold',
        fill: am5.color('#666666'),
        paddingRight: 15
      })
    );

    // Hide all grid lines
    yAxis.get('renderer').grid.template.set('forceHidden', true);
    xAxis.get('renderer').grid.template.set('forceHidden', true);

    // Ensure axis baselines are visible and styled
    yAxis.get('renderer').set('strokeOpacity', 1);
    yAxis.get('renderer').set('stroke', am5.color('#dedede'));
    yAxis.get('renderer').set('strokeWidth', 1);

    xAxis.get('renderer').set('strokeOpacity', 1);
    xAxis.get('renderer').set('stroke', am5.color('#dedede'));
    xAxis.get('renderer').set('strokeWidth', 1);

    // Create single series
    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Responses',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'cumulativeCount',
        valueXField: 'date',
        categoryXField: 'date',
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'horizontal',
          labelText: 'Responses: {valueY}'
        })
      })
    );

    // Set line color - using first color from categorical colors
    const lineColor = am5.color('#336F9C'); // Blue color from your palette

    series.set('stroke', lineColor);

    // Set line properties
    series.strokes.template.setAll({
      strokeWidth: 3,
      stroke: lineColor
    });

    series.set('fill', lineColor);

    series.fills.template.setAll({
      fillOpacity: 0,
      visible: true,
      fill: lineColor
    });

    // Prepare and set data
    const chartData = data.map((item) => ({
      date: new Date(item.date).getTime(),
      cumulativeCount: item.cumulativeCount,
      cumulativePercentage: item.cumulativePercentage || 0
    }));

    series.data.setAll(chartData);

    if (isFirstLoad) {
      // Make stuff animate on load
      series.appear(1000);
      chart.appear(1000, 100);
    }

    return () => {
      root.dispose();
    };
  }, [data]);

  return (
    <Box>
      <ChartHeader
        title='Number of responses over time'
        description='This chart shows the overall cumulative response count over time.'
        showRefreshButton={data && data.length > 0}
        handleManualRefresh={handleManualRefresh}
        isRefreshing={isManualRefreshing}
      />

      {!data || data?.length === 0 ? (
        <Box sx={{ mt: 2 }}>
          <NoResponsesAvailableYet data={data} onRefresh={onRefresh} />
        </Box>
      ) : (
        <Box ref={chartRef} style={{ width: '100%', height: 'calc(100vh - 380px)' }} />
      )}
    </Box>
  );
};

export default SingleCompletionOverTimeChart;
